import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Confirm,
  Dimmer,
  Dropdown,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Progress,
} from 'semantic-ui-react';
import styled from 'styled-components';
import Button from '../../../../components/Button';
import {
  dangerNotification,
  successNotification,
} from '../../../../services/notification';
import {
  getBatchTransferStatus,
  getBatchTransferTargets,
  previewTransferPolicies,
  TransferPreviewItem,
  TransferStatusResponse,
  TransferTargetBatch,
  transferBatchPolicies,
} from '../../../../services/fire-insurance-batches';
import {
  buildTransferConfirmationMessage,
  getTransferablePolicyIds,
  hasTransferablePreviewItems,
  summarizeTransferPreview,
} from '../utils/batch-transfer.util';

export interface TransferPolicyRow {
  id: string;
  tenantName: string;
  tenantDocument: string;
}

export interface TransferPoliciesModalSourceBatch {
  id: string;
  batchNumber: number;
  estate: {
    name: string;
  };
}

interface TransferPoliciesModalProps {
  open: boolean;
  sourceBatch: TransferPoliciesModalSourceBatch | null;
  selectedPolicies: TransferPolicyRow[];
  onClose: () => void;
  onSuccess: () => void;
}

type ModalPhase = 'configure' | 'confirm' | 'executing' | 'done';

const ContextBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const ContextItem = styled.span`
  color: #444;
  font-size: 13px;

  strong {
    color: #333;
  }
`;

const PreviewList = styled(List)`
  &.ui.list {
    margin: 12px 0 0;
  }
`;

const TransferPoliciesModal: React.FC<TransferPoliciesModalProps> = ({
  open,
  sourceBatch,
  selectedPolicies,
  onClose,
  onSuccess,
}) => {
  const [phase, setPhase] = useState<ModalPhase>('configure');
  const [targets, setTargets] = useState<TransferTargetBatch[]>([]);
  const [targetBatchId, setTargetBatchId] = useState<string>('');
  const [previewItems, setPreviewItems] = useState<TransferPreviewItem[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transferId, setTransferId] = useState<string | null>(null);
  const [transferStatus, setTransferStatus] =
    useState<TransferStatusResponse | null>(null);

  const policyIds = useMemo(
    () => selectedPolicies.map((policy) => policy.id),
    [selectedPolicies],
  );

  const resetState = useCallback(() => {
    setPhase('configure');
    setTargets([]);
    setTargetBatchId('');
    setPreviewItems([]);
    setLoadingTargets(false);
    setLoadingPreview(false);
    setSubmitting(false);
    setTransferId(null);
    setTransferStatus(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (!sourceBatch) {
      return;
    }

    const loadTargets = async () => {
      setLoadingTargets(true);
      try {
        const response = await getBatchTransferTargets(sourceBatch.id);
        setTargets(response);
      } catch (error) {
        dangerNotification(
          'Erro',
          error instanceof Error
            ? error.message
            : 'Não foi possível carregar lotes destino.',
        );
      } finally {
        setLoadingTargets(false);
      }
    };

    loadTargets();
  }, [open, sourceBatch, resetState]);

  useEffect(() => {
    if (!open || !sourceBatch || !targetBatchId || policyIds.length === 0) {
      setPreviewItems([]);
      return;
    }

    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const response = await previewTransferPolicies(sourceBatch.id, {
          targetBatchId,
          policyIds,
        });
        setPreviewItems(response);
      } catch (error) {
        setPreviewItems([]);
        dangerNotification(
          'Erro',
          error instanceof Error
            ? error.message
            : 'Não foi possível consultar itens na Yelum.',
        );
      } finally {
        setLoadingPreview(false);
      }
    };

    loadPreview();
  }, [open, sourceBatch, targetBatchId, policyIds]);

  useEffect(() => {
    if (phase !== 'executing' || !transferId) {
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getBatchTransferStatus(transferId);
        if (cancelled) {
          return;
        }
        setTransferStatus(status);

        if (status.status === 'COMPLETED') {
          setPhase('done');
          const certErrors = status.items.filter(
            (item) => item.status === 'certificate_error',
          );
          if (certErrors.length > 0) {
            successNotification(
              'Transferência concluída',
              'Apólices transferidas. Alguns certificados serão reprocessados automaticamente à noite.',
            );
          } else {
            successNotification(
              'Sucesso',
              'Transferência de apólices concluída com sucesso.',
            );
          }
          onSuccess();
          onClose();
          return;
        }

        if (status.status === 'FAILED') {
          dangerNotification(
            'Falha na transferência',
            status.errorMessage || 'Não foi possível concluir a transferência.',
          );
          setPhase('configure');
        }
      } catch (error) {
        if (!cancelled) {
          dangerNotification(
            'Erro',
            error instanceof Error
              ? error.message
              : 'Falha ao consultar progresso da transferência.',
          );
        }
      }
    };

    poll();
    const interval = window.setInterval(poll, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [phase, transferId, onSuccess, onClose]);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.id === targetBatchId),
    [targets, targetBatchId],
  );

  const targetOptions = useMemo(
    () =>
      targets.map((target) => ({
        key: target.id,
        value: target.id,
        text: `Lote #${target.batchNumber} — ${target.quotationCode}${
          target.proposalCode ? '' : ' (sem proposta)'
        }`,
      })),
    [targets],
  );

  const previewSummary = useMemo(
    () => summarizeTransferPreview(previewItems),
    [previewItems],
  );

  const transferablePolicyIds = useMemo(
    () => getTransferablePolicyIds(previewItems),
    [previewItems],
  );

  const canConfirm = useMemo(
    () =>
      Boolean(targetBatchId) &&
      Boolean(selectedTarget?.proposalCode?.trim()) &&
      hasTransferablePreviewItems(previewItems) &&
      !loadingPreview &&
      !submitting,
    [targetBatchId, selectedTarget, previewItems, loadingPreview, submitting],
  );

  const confirmationMessage = useMemo(
    () =>
      buildTransferConfirmationMessage(
        previewSummary.transferable,
        previewSummary.remaining,
      ),
    [previewSummary],
  );

  const handleClose = () => {
    if (submitting || phase === 'executing') {
      return;
    }
    onClose();
  };

  const handleRequestConfirm = () => {
    if (!sourceBatch || !canConfirm) {
      return;
    }
    setPhase('confirm');
  };

  const handleConfirm = async () => {
    if (!sourceBatch || !canConfirm || transferablePolicyIds.length === 0) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await transferBatchPolicies(sourceBatch.id, {
        targetBatchId,
        policyIds: transferablePolicyIds,
      });
      setTransferId(response.transferId);
      setPhase('executing');
    } catch (error) {
      dangerNotification(
        'Erro',
        error instanceof Error
          ? error.message
          : 'Não foi possível iniciar a transferência.',
      );
      setPhase('configure');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPercent = useMemo(() => {
    if (!transferStatus?.items?.length) {
      return 0;
    }
    const doneCount = transferStatus.items.filter((item) =>
      ['done', 'certificate_error'].includes(item.status),
    ).length;
    return Math.round((doneCount / transferStatus.items.length) * 100);
  }, [transferStatus]);

  const renderPreviewItem = (item: TransferPreviewItem) => {
    if (item.status === 'matched') {
      return (
        <Message positive size="small">
          <strong>{item.tenantName}</strong> — {item.tenantDocument}
          <br />
          Item Yelum: <strong>{item.matchedItemNo}</strong>
          {item.previousItemNo &&
          item.previousItemNo !== String(item.matchedItemNo) ? (
            <span> (local era :{item.previousItemNo})</span>
          ) : null}
        </Message>
      );
    }

    if (item.status === 'not_found') {
      return (
        <Message negative size="small">
          <strong>Item não encontrado</strong>
          <br />
          {item.tenantName} — {item.tenantDocument}
          {item.message ? <div>{item.message}</div> : null}
        </Message>
      );
    }

    if (item.status === 'collision') {
      return (
        <Message negative size="small">
          <strong>Conflito de item Yelum</strong>
          <br />
          {item.tenantName} — {item.tenantDocument}
          {item.message ? <div>{item.message}</div> : null}
        </Message>
      );
    }

    return (
      <Message warning size="small">
        <strong>Match ambíguo</strong>
        <br />
        {item.tenantName} — {item.tenantDocument}
        {item.message ? <div>{item.message}</div> : null}
      </Message>
    );
  };

  if (!sourceBatch) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeOnDimmerClick={!submitting && phase !== 'executing'}
      >
        <Modal.Header>
          <Icon name="exchange" />
          Transferir apólices
        </Modal.Header>
        <Modal.Content scrolling>
          <ContextBanner>
            <ContextItem>
              <strong>Lote origem:</strong> #{sourceBatch.batchNumber}
            </ContextItem>
            <ContextItem>
              <strong>Imobiliária:</strong> {sourceBatch.estate.name}
            </ContextItem>
            <ContextItem>
              <strong>Selecionadas:</strong> {selectedPolicies.length}
            </ContextItem>
          </ContextBanner>

          {phase === 'configure' && (
            <>
              <p style={{ marginBottom: 12, color: '#444' }}>
                Escolha o lote destino. O sistema consultará a Yelum e fará o match
                por inquilino/endereço (o número do item pode ter mudado).
              </p>

              <Dropdown
                fluid
                selection
                search
                loading={loadingTargets}
                placeholder="Selecione o lote destino"
                options={targetOptions}
                value={targetBatchId || undefined}
                onChange={(_, data) => setTargetBatchId(String(data.value || ''))}
                disabled={loadingTargets || targets.length === 0}
              />

              {targets.length === 0 && !loadingTargets && (
                <Message warning style={{ marginTop: 12 }}>
                  Nenhum lote aberto elegível encontrado para esta imobiliária
                  (com proposta cadastrada).
                </Message>
              )}

              {selectedTarget && !selectedTarget.proposalCode?.trim() && (
                <Message warning style={{ marginTop: 12 }}>
                  O lote destino selecionado não possui código de proposta. Atualize
                  os códigos do lote antes de transferir.
                </Message>
              )}

              {targetBatchId && (
                <Dimmer active={loadingPreview} inverted>
                  <Loader active inline="centered">
                    Consultando itens na Yelum...
                  </Loader>
                </Dimmer>
              )}

              {previewItems.length > 0 && (
                <>
                  <Message
                    info={previewSummary.remaining === 0}
                    warning={previewSummary.remaining > 0}
                    style={{ marginTop: 12 }}
                  >
                    {previewSummary.transferable} de {previewSummary.total}{' '}
                    apólice(s) serão transferidas.
                    {previewSummary.remaining > 0
                      ? ` ${previewSummary.remaining} permanecerão no lote atual por conflito, item não encontrado ou match ambíguo.`
                      : ''}
                  </Message>
                  <PreviewList divided relaxed>
                    {previewItems.map((item) => (
                      <List.Item key={item.policyId}>
                        {renderPreviewItem(item)}
                      </List.Item>
                    ))}
                  </PreviewList>
                </>
              )}
            </>
          )}

          {phase === 'executing' && (
            <>
              <p style={{ marginBottom: 12 }}>Processando transferência...</p>
              <Progress percent={progressPercent} indicating progress />
              <PreviewList divided relaxed>
                {(transferStatus?.items ?? []).map((item) => (
                  <List.Item key={item.policyId}>
                    <Label
                      color={
                        item.status === 'done'
                          ? 'green'
                          : item.status === 'certificate_error'
                            ? 'orange'
                            : 'blue'
                      }
                    >
                      {item.tenantName}
                    </Label>{' '}
                    {item.step || item.status}
                    {item.error ? ` — ${item.error}` : ''}
                  </List.Item>
                ))}
              </PreviewList>
            </>
          )}

          {phase === 'done' && (
            <Message positive>
              Transferência concluída. Atualize a página se necessário.
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            onClick={handleClose}
            disabled={submitting || phase === 'executing'}
          >
            {phase === 'done' ? 'Fechar' : 'Cancelar'}
          </Button>
          {phase === 'configure' && (
            <Button
              color="purple"
              loading={submitting}
              disabled={!canConfirm}
              onClick={handleRequestConfirm}
            >
              Confirmar transferência
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      <Confirm
        open={phase === 'confirm'}
        header="Confirmar transferência de apólices"
        content={confirmationMessage}
        confirmButton="Transferir"
        cancelButton="Voltar"
        onCancel={() => setPhase('configure')}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default TransferPoliciesModal;
